<template>
    <Teleport to="body">
        <div class="booking-dialog-backdrop" :class="bookingThemeClass">
            <div
                ref="dialog"
                class="booking-dialog"
                data-palette="noshow"
                :style="bookingThemeStyle"
                role="dialog"
                aria-modal="true"
                aria-labelledby="booking-dialog-title"
                tabindex="-1">
                <header class="booking-dialog-header">
                    <div>
                        <p class="booking-dialog-eyebrow">{{ $tl("labels.reservation") }}</p>
                        <h2 id="booking-dialog-title">{{ $tl("labels.booking.reservations.add.title") }}</h2>
                        <p>{{ $t("reservationForm.subtitle") }}</p>
                    </div>
                    <button
                        type="button"
                        class="booking-dialog-close"
                        :disabled="busy"
                        :aria-label="$t('reservationForm.close')"
                        @click="requestClose">
                        <feather type="x" aria-hidden="true" />
                    </button>
                </header>
                <div v-if="confirmDiscard" class="booking-discard" role="alert">
                    <strong>{{ $t("reservationForm.discard") }}</strong>
                    <button ref="continueButton" type="button" class="booking-button booking-button-secondary" @click="keepEditing">
                        {{ $t("reservationForm.keepEditing") }}
                    </button>
                    <button type="button" class="booking-button booking-button-danger" @click="$emit('close')">
                        {{ $t("reservationForm.discardConfirm") }}
                    </button>
                </div>
                <AddReservation
                    ref="form"
                    :restaurant_id="restaurant_id"
                    :slotIdToPreselect="slot_id"
                    :serviceIdToPreselect="service_id"
                    :tablesIdToPreselect="tablesIdToPreselect"
                    :date="date"
                    :defaultValues="defaultValues"
                    :showWaitingListButton="showWaitingListButton"
                    @busy="busy = $event"
                    @cancel="requestClose"
                    @saved="$emit('reservation-saved')" />
            </div>
        </div>
    </Teleport>
</template>

<script>
import { Teleport } from "vue";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import AddReservation from "../../reservations/add/AddReservation.vue";
import BookingThemeMixin from "../../reservations/add/bookingThemeMixin.js";

export default {
    components: { AddReservation, Teleport },
    mixins: [BookingThemeMixin],
    props: {
        restaurant_id: { default: "" },
        slot_id: { default: null },
        service_id: { default: null },
        date: { default: null },
        defaultValues: { type: Object, default: null },
        showWaitingListButton: { type: Boolean, default: true },
        tablesIdToPreselect: { type: Array, default: () => [] },
    },
    emits: ["close", "reservation-saved"],
    data: () => ({ busy: false, confirmDiscard: false, previousFocus: null, previousInert: false, scrollTarget: null }),
    methods: {
        requestClose() {
            if (this.busy) return;
            if (!this.$refs.form.hasChanges) {
                this.$emit("close");
                return;
            }
            this.confirmDiscard = true;
            this.$nextTick(() => this.$refs.continueButton?.focus());
        },
        keepEditing() {
            this.confirmDiscard = false;
            this.$refs.dialog.focus({ preventScroll: true });
        },
        onKeydown(event) {
            if (event.key === "Escape") {
                event.preventDefault();
                if (this.confirmDiscard) this.keepEditing();
                else this.requestClose();
                return;
            }
            if (event.key !== "Tab") return;
            const dialog = [...document.querySelectorAll('[role="dialog"]')].at(-1) || this.$refs.dialog;
            const focusable = [...dialog.querySelectorAll("button, input, select, textarea, a[href], summary, [tabindex]")].filter(
                (element) => !element.matches(":disabled") && element.tabIndex >= 0 && element.getClientRects().length > 0
            );
            const first = focusable[0],
                last = focusable.at(-1);
            if (!first) {
                event.preventDefault();
                dialog.focus();
                return;
            }
            if (
                event.shiftKey &&
                (document.activeElement === first || document.activeElement === dialog || !dialog.contains(document.activeElement))
            ) {
                event.preventDefault();
                last.focus();
            } else if (
                !event.shiftKey &&
                (document.activeElement === last || document.activeElement === dialog || !dialog.contains(document.activeElement))
            ) {
                event.preventDefault();
                first.focus();
            }
        },
    },
    mounted() {
        this.previousFocus = document.activeElement;
        const app = document.getElementById("app");
        this.previousInert = app?.inert || false;
        if (app) app.inert = true;
        this.scrollTarget = this.$refs.dialog.querySelector(".booking-form-scroll");
        disableBodyScroll(this.scrollTarget, { reserveScrollBarGap: true });
        document.addEventListener("keydown", this.onKeydown);
        this.$nextTick(() => this.$refs.dialog.focus({ preventScroll: true }));
    },
    beforeUnmount() {
        document.removeEventListener("keydown", this.onKeydown);
        enableBodyScroll(this.scrollTarget);
        const app = document.getElementById("app");
        if (app) app.inert = this.previousInert;
        if (this.previousFocus?.isConnected) this.previousFocus.focus();
    },
};
</script>
